import React, { useState } from "react";
import { List, Tag, Button, Checkbox, Pagination, Space } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import { Circle, Dot } from "lucide-react";
import { useMediaQuery } from "react-responsive";

import moment from "moment";
import { Link } from "react-router-dom";

// Utility to generate URL for notification redirection
function getNotificationUrl(notification) {
  if (!notification.data) return "#";
  const { redirection, order_key } = notification.data;
  if (!redirection) return "#";

  if (redirection.includes("Accept_Reject_Page")) {
    return `/order-management/order-operations/accept-reject-orders`;
  }
  if (redirection.includes("Transactions")) {
    return `/payment-operations/transactions`;
  }
  if (redirection.includes("Task_Detail")) {
    return `/order-management/my-orders/${order_key}`;
  }
  if (redirection.includes("Help_Center")) {
    return `/help-center`;
  }
  if (redirection.includes("Comment")) {
    return `/order-management/my-orders/${order_key}?type=Comment`;
  }
  if (redirection.includes("Feedback")) {
    return `/order-management/my-orders/${order_key}?type=Feedback`;
  }
  if (redirection.includes("Solution")) {
    return `/order-management/my-orders/${order_key}?type=Solution`;
  }
  return "#";
}

const NotificationList = ({
  notifications = [],
  onMarkAsRead,

  onNotificationClick,
  pagination,
  setPagination,
  onMarkSelectedAsUnread,
}) => {
  // Handles click on notification link: marks as read if unread, then navigates
  const handleNotificationLinkClick = async (e, item) => {
    if (item.unread && onMarkAsRead) {
      console.log("mark as read");
      await onMarkAsRead([item.uid]);
    }
  };

  const [expandedItems, setExpandedItems] = useState({});
  const [selectedNotificationIds, setSelectedNotificationIds] = useState([]);
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  // Select All logic
  const allIds = notifications.map((item) => item.uid);
  const isAllSelected =
    allIds.length > 0 && selectedNotificationIds.length === allIds.length;
  const isIndeterminate =
    selectedNotificationIds.length > 0 &&
    selectedNotificationIds.length < allIds.length;

  const handleSelectAll = (e) => {
    setSelectedNotificationIds(e.target.checked ? allIds : []);
  };

  const handleSelectOne = (uid) => (e) => {
    setSelectedNotificationIds((prev) =>
      e.target.checked ? [...prev, uid] : prev.filter((id) => id !== uid)
    );
  };

  // Bulk mark as read/unread
  const handleBulkMarkAsRead = () => {
    if (onMarkAsRead) onMarkAsRead(selectedNotificationIds);
    setSelectedNotificationIds([]);
  };
  const handleBulkMarkAsUnread = () => {
    if (onMarkSelectedAsUnread) onMarkSelectedAsUnread(selectedNotificationIds);
    setSelectedNotificationIds([]);
  };
  const toggleExpand = (uid) => {
    setExpandedItems((prev) => ({
      ...prev,
      [uid]: !prev[uid],
    }));
  };

  const onPageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize,
    }));
  };

  // Styled old -> new diff for order-change notifications
  const renderOrderChange = (data, uid) => {
    const label = data.field_label || "Value";
    const oldVal = data.old_value ?? "-";
    const newVal = data.new_value ?? "-";
    const isExpanded = expandedItems[uid];
    const LIMIT = 60;
    const clip = (s) =>
      !isExpanded && String(s).length > LIMIT
        ? String(s).slice(0, LIMIT) + "..."
        : String(s);
    const longEnough =
      String(oldVal).length > LIMIT || String(newVal).length > LIMIT;
    const chip = (text, kind) => (
      <span
        style={{
          padding: "1px 6px",
          borderRadius: 4,
          fontSize: 13,
          background: kind === "old" ? "#FEE2E2" : "#DCFCE7",
          color: kind === "old" ? "#B91C1C" : "#166534",
          textDecoration: kind === "old" ? "line-through" : "none",
          wordBreak: "break-word",
        }}
      >
        {clip(text)}
      </span>
    );
    return (
      <span style={{ display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
        <span style={{ color: "#374151", fontWeight: 600 }}>{label}:</span>
        {chip(oldVal, "old")}
        <span style={{ color: "#6B7280" }}>→</span>
        {chip(newVal, "new")}
        {longEnough && (
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(uid);
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {isExpanded ? "Show Less" : "Show More"}
          </Button>
        )}
      </span>
    );
  };

  const renderPreview = (preview, uid) => {
    if (!preview) {
      return null; // or a fallback UI like <div>No preview available</div>
    }
    const words = preview.split(" ");
    const isExpanded = expandedItems[uid];
    const shouldCollapse = words.length > 10;

    if (shouldCollapse && !isExpanded) {
      return (
        <>
          "{words.slice(0, 10).join(" ")}..."
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(uid);
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            Show More
          </Button>
        </>
      );
    }

    return (
      <>
        "{preview}"
        {shouldCollapse && (
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(uid);
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            Show Less
          </Button>
        )}
      </>
    );
  };

  return (
    <Flex vertical style={{ width: "100%" }} align="flex-end">
      {/* Bulk Actions and Select All */}
      <div
        style={{
          width: "100%",
          marginBottom: 12,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: isMobile ? "flex-start" : "space-between",
          alignItems: isMobile ? "start" : "center",
          gap: isMobile ? 12 : 0,
        }}
      >
        <Checkbox
          indeterminate={isIndeterminate}
          checked={isAllSelected}
          onChange={handleSelectAll}
          style={isMobile ? { marginBottom: 8 } : {}}
        >
          {selectedNotificationIds.length === 0
            ? "Select All"
            : `${selectedNotificationIds.length} selected`}
        </Checkbox>
        <Space
          direction={isMobile ? "vertical" : "horizontal"}
          style={{ width: isMobile ? "100%" : "auto" }}
        >
          <Button
            type="primary"
            style={{
              background:
                selectedNotificationIds.length === 0
                  ? "transparent"
                  : "transparent",
              borderColor:
                selectedNotificationIds.length === 0 ? "grey" : "#36c6d3",
              color: selectedNotificationIds.length === 0 ? "grey" : "#36c6d3",
              width: isMobile ? "100%" : undefined,
            }}
            disabled={selectedNotificationIds.length === 0}
            onClick={handleBulkMarkAsRead}
          >
            {selectedNotificationIds.length > 0
              ? `Mark ${selectedNotificationIds.length} as Read`
              : "Mark Selected as Read"}
          </Button>
          <Button
            type="default"
            style={{
              borderColor:
                selectedNotificationIds.length === 0 ? "transparent" : "#000",
              width: isMobile ? "100%" : undefined,
            }}
            disabled={selectedNotificationIds.length === 0}
            onClick={handleBulkMarkAsUnread}
          >
            {selectedNotificationIds.length > 0
              ? `Mark ${selectedNotificationIds.length} as Unread`
              : "Mark Selected as Unread"}
          </Button>
        </Space>
      </div>
      <ul
        style={{
          width: "100%",
          background: "transparent",
          height: "100%",
          listStyle: "none",
          padding: 0,
          margin: 0,
        }}
      >
        {notifications.map((item) => {
          const checked = selectedNotificationIds.includes(item.uid);
          return (
            <li
              key={item.uid}
              style={{
                borderBottom: "1px solid #D1D5DB",
                borderRadius: 4,
                padding: isMobile ? 0 : 16,
                marginBottom: 12,
                cursor: "pointer",
                background: checked ? "#f0f5ff" : undefined,
                width: "100%",
              }}
            >
              <Flex
                justify="space-between"
                align={isMobile ? "flex-end" : "center"}
                dir={isMobile ? "flex-column" : "flex-row"}
                style={{
                  width: "100%",
                  flexDirection: isMobile ? "column" : "row",
                }}
              >
                {/* Checkbox for item */}

                <Flex
                  style={{ width: isMobile ? "100%" : "80%" }}
                  align="flex-start"
                >
                  <Checkbox
                    className="notification-checkbox"
                    checked={checked}
                    onChange={handleSelectOne(item.uid)}
                    style={{ marginRight: 12 }}
                    onClick={(e) => e.stopPropagation()} // Prevent parent click
                    onMouseDown={(e) => e.stopPropagation()} // Prevent parent onMouseDown
                  />
                  <Flex
                    style={{ maxWidth: isMobile ? "100%" : "80%" }}
                    vertical
                    align="flex-start"
                  >
                    <a
                      href={getNotificationUrl(item)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ cursor: "pointer", color: "#000" }}
                      onClick={(e) => handleNotificationLinkClick(e, item)}
                    >
                      <span dangerouslySetInnerHTML={{ __html: item.verb }} />
                    </a>

                    <Flex
                      align={expandedItems[item.uid] ? "flex-start" : "center"}
                      gap={10}
                    >
                      <p
                        style={{
                          color: "#6B7280",
                          margin: 0,
                          padding: 0,
                          textAlign: "left",
                        }}
                      >
                        {item?.data?.type === "order_change" &&
                        (item?.data?.old_value !== undefined ||
                          item?.data?.new_value !== undefined)
                          ? renderOrderChange(item.data, item.uid)
                          : item?.data?.preview
                          ? renderPreview(item?.data?.preview, item.uid)
                          : null}
                      </p>
                      <p
                        style={{
                          color: "#6B7280",
                          margin: 0,
                          padding: 0,
                          textAlign: "left",
                          width: "max-content",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item?.data?.file_count
                          ? `Files Attached: ${item?.data?.file_count} `
                          : null}
                      </p>
                    </Flex>
                  </Flex>
                </Flex>
                <Flex
                  style={{
                    height: "max-content",
                    flexDirection: isMobile ? "row-reverse" : "column",
                  }}
                  align={isMobile ? "center" : "flex-end"}
                  gap={isMobile ? 10 : 0}
                  justify="flex-end"
                >
                  {item?.data?.type ? (
                    <Tag color="#040724" style={{ width: "max-content" }}>
                      {item.data.type}
                    </Tag>
                  ) : null}
                  <Flex align="center" style={{marginTop:'3px'}}>
                    {item.unread && <Dot color="#1C64F2" size={40} />}

                    <p
                      style={{
                        color: "#8c8c8c",
                        fontSize: 12,
                        margin: 0,
                        padding: 0,
                      }}
                    >
                      <ClockCircleOutlined />{" "}
                      {moment(item.timestamp)
                        .local()
                        .format("DD-MM-YYYY hh:mm a")}
                    </p>
                  </Flex>
                </Flex>
              </Flex>
            </li>
          );
        })}
      </ul>
      <Pagination
        current={pagination.current}
        pageSize={pagination.pageSize}
        total={pagination.total}
        onChange={onPageChange}
        style={{ marginTop: 20, textAlign: "center" }}
      />
    </Flex>
  );
};

export default NotificationList;
